// /app/frontend/src/pages/AcceptInvite.jsx
//
// Public landing page that handles workspace invite acceptance.
// Flow:
//   1. Page loads at /invite/:token — fetches public invite details (no auth)
//   2. Shows "X invited you to Y workspace" + Accept button
//   3. If user not logged in → redirects to /login or /register with the token
//      preserved as ?next=/invite/{token}
//   4. If logged in → POST /workspace/invites/{token}/accept → redirect to /app/team

import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Users, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/workspace/invites/${token}`)
      .then((r) => { if (alive) setInvite(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Invite invalid or expired."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      await api.post(`/workspace/invites/${token}/accept`);
      toast.success(`Welcome to ${invite.workspace_name}!`);
      nav("/v3/business/team");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept invite.");
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <CenterCard>
        <Loader2 size={32} className="mx-auto animate-spin text-[var(--ink-2)]" />
      </CenterCard>
    );
  }

  if (error) {
    return (
      <CenterCard>
        <AlertTriangle size={42} className="mx-auto text-amber-600 mb-3" />
        <h1 className="font-display font-extrabold text-2xl mb-1" data-testid="invite-error-title">Invite unavailable</h1>
        <p className="text-[var(--ink-2)] text-sm mb-5">{error}</p>
        <Link to="/" className="btn-outline">Back to home</Link>
      </CenterCard>
    );
  }

  return (
    <CenterCard>
      <Users size={42} className="mx-auto text-emerald-600 mb-3" />
      <h1 className="font-display font-extrabold text-2xl mb-1" data-testid="invite-title">
        Join {invite.workspace_name}
      </h1>
      <p className="text-[var(--ink-2)] text-sm mb-1">
        <b>{invite.inviter_name}</b> invited you as a <b>{invite.role}</b>
      </p>
      <p className="text-xs text-[var(--ink-3)] mb-6" data-testid="invite-email">
        Sent to {invite.invited_email}
      </p>

      {user ? (
        user.email.toLowerCase() === invite.invited_email.toLowerCase() ? (
          <button
            type="button"
            className="btn-money w-full"
            onClick={accept}
            disabled={accepting}
            data-testid="invite-accept-btn"
          >
            {accepting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Accept & join workspace
          </button>
        ) : (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="invite-wrong-account">
            You're signed in as <b>{user.email}</b>, but this invite was sent to <b>{invite.invited_email}</b>.
            <div className="mt-3">
              <Link to={`/login?next=/invite/${token}`} className="text-amber-700 underline font-semibold">
                Sign in as {invite.invited_email}
              </Link>
            </div>
          </div>
        )
      ) : (
        <div className="flex flex-col gap-2">
          <Link to={`/register?email=${encodeURIComponent(invite.invited_email)}&next=/invite/${token}`}
                className="btn-money" data-testid="invite-register-cta">
            Create account & join
          </Link>
          <Link to={`/login?email=${encodeURIComponent(invite.invited_email)}&next=/invite/${token}`}
                className="btn-outline" data-testid="invite-login-cta">
            I already have an account
          </Link>
        </div>
      )}
    </CenterCard>
  );
}

function CenterCard({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[var(--surface)]" data-testid="invite-page">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {children}
      </div>
    </div>
  );
}
